import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { v4 as uuid } from 'uuid'
import { appConfig, constants } from '../../config'
import { buildSocrClientCatalog } from '../../utils/socrCatalogAdapter'

const { graphFilters } = constants

const processJiraAndZendesk = (jiraResults, zendeskResults) => {
  const results = { orgZendeskTickets: {}, vulnerabilityIssues: [], upgradeIssues: [] }
  if (jiraResults?.status !== 200 || !Array.isArray(jiraResults?.data) || !jiraResults.data.length) return results

  const jiraIssues = jiraResults.data.reduce((obj, resultInfo) => {
    if (typeof resultInfo === 'object' && Boolean(resultInfo) && Boolean(resultInfo.jiraIssueKey)) {
      obj[resultInfo.jiraIssueKey] = {
        ...(obj[resultInfo.jiraIssueKey] || {}),
        ...resultInfo
      }
    }
    return obj
  }, {})

  const orgZendeskTickets = (zendeskResults?.data || []).reduce((obj, resultInfo) => {
    if (typeof resultInfo !== 'object' || !resultInfo || !resultInfo.id) return obj
    const splitTickets = (resultInfo.jiraTicket || '').split(',').map(s => s.trim())
    const startDateObj = new Date(resultInfo.zdRequestDate)

    if (Array.isArray(splitTickets) && splitTickets.length) {
      splitTickets.forEach((jiraKey) => {
        if (typeof jiraKey === 'string' && jiraKey && typeof jiraIssues[jiraKey] === 'object' && jiraIssues[jiraKey]) {
          const jiraInfo = jiraIssues[jiraKey]
          const clientName = (jiraInfo.jiraProjectName || '').trim().replace(/^"+|"+$/g, '')
          const endDateObj = new Date(jiraInfo.jiraResolutionDate || resultInfo.zdSolvedDate)
          const [startDate] = new Date(startDateObj).toISOString().split('T')
          const [endDate] = new Date(endDateObj).toISOString().split('T')
          obj[clientName] = [
            ...(obj[clientName] || []),
            {
              ...jiraInfo,
              ...resultInfo,
              zendeskID: resultInfo.id,
              supportType: graphFilters.SUPPORT,
              startDate,
              endDate
            }
          ]
        } else if (resultInfo.isJiraEscalated) {
          if (!obj.orphans) obj.orphans = []
          obj.orphans.push({ ...resultInfo, jiraKey })
        }
      })
    } else {
      if (!obj.orphans) obj.orphans = []
      obj.orphans.push(resultInfo)
    }
    return obj
  }, { orphans: [] })

  results.orgZendeskTickets = orgZendeskTickets
  return results
}

const processVulnerabilities = (vulnerabilityResults) => {
  const result = {}
  if (vulnerabilityResults?.status !== 200 || !Array.isArray(vulnerabilityResults?.data) || !vulnerabilityResults.data.length) return result
  const supportType = graphFilters.VULNERABILITIES
  vulnerabilityResults.data.forEach((resultInfo) => {
    if (typeof resultInfo !== 'object' || !resultInfo?.jiraIssueKey) return
    const { jiraProjectName, createdDate, jiraResolutionDate, storyPointsActual } = resultInfo
    const startDateObj = new Date(createdDate)
    const endDateObj = new Date(jiraResolutionDate)
    const [startDate] = new Date(startDateObj).toISOString().split('T')
    const [endDate] = new Date(endDateObj).toISOString().split('T')
    result[jiraProjectName] = [
      ...(result[jiraProjectName] || []),
      {
        ...resultInfo,
        startDate,
        endDate,
        supportType,
        jiraIssueId: uuid(),
        isBillable: true,
        revisedSP: storyPointsActual || 0
      }
    ]
  })
  return result
}

const processUpgrades = (upgradeResults) => {
  const result = {}
  if (upgradeResults?.status !== 200 || !Array.isArray(upgradeResults?.data) || !upgradeResults.data.length) return result
  const supportType = graphFilters.UPGRADES
  upgradeResults.data.forEach((resultInfo) => {
    if (typeof resultInfo !== 'object' || !resultInfo?.jiraIssueKey) return
    const { jiraProjectName, createdDate, jiraResolutionDate, storyPointsActual } = resultInfo
    const startDateObj = new Date(createdDate)
    const endDateObj = new Date(jiraResolutionDate)
    const [startDate] = new Date(startDateObj).toISOString().split('T')
    const [endDate] = new Date(endDateObj).toISOString().split('T')
    result[jiraProjectName] = [
      ...(result[jiraProjectName] || []),
      {
        ...resultInfo,
        startDate,
        endDate,
        supportType,
        jiraIssueId: uuid(),
        isBillable: true,
        revisedSP: storyPointsActual || 0
      }
    ]
  })
  return result
}

const processQbo = (qboResults) => {
  const result = { byCustomer: {} }
  if (qboResults?.status !== 200 || !Array.isArray(qboResults?.data) || !qboResults.data.length) return result

  qboResults.data.forEach((row) => {
    if (!row || typeof row !== 'object') return
    const customerName = (row.qboCustomerName || '').trim().replace(/^"+|"+$/g, '')
    if (!customerName) return

    const dateRaw = row.qboInvoiceDate
    const dateObj = dateRaw ? new Date(dateRaw) : null
    const [startDate] = (dateObj && !Number.isNaN(dateObj.getTime())
      ? dateObj
      : new Date()).toISOString().split('T')

    const normalized = {
      ...row,
      startDate,
      endDate: startDate,
      value: Number(row.qboInvoiceItemLineAmt) || 0,
      chargeName: (row.qboInvoiceItemLineDesc || '').trim(),
      lineType: (row.qboInvoiceItemLineType || '').trim(),
      customerName
    }

    result.byCustomer[customerName] = [
      ...(result.byCustomer[customerName] || []),
      normalized
    ]
  })

  return result
}

const fetchTickets = createAsyncThunk(
  'api/fetchTickets',
  async ({ onSuccess = () => {}, onFailure = () => {} }, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token
      const [jiraResults, zendeskResults] = await Promise.all([
        axios.get(`${appConfig.apiURL}/api/jira`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${appConfig.apiURL}/api/zendesk`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ])

      const results = processJiraAndZendesk(jiraResults, zendeskResults)
      return { ...results, vulnerabilityIssues: [], upgradeIssues: [] }
    } catch (err) {
      console.error(err)
      return rejectWithValue(false)
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const state = getState()?.api || {}
      if (state.loading) return false
      const hasOrgTickets = state.organizationTickets && Object.keys(state.organizationTickets).length > 0
      return !hasOrgTickets
    }
  }
)

const fetchVulnerabilitiesAndUpgrades = createAsyncThunk(
  'api/fetchVulnerabilitiesAndUpgrades',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token
      const [vulnerabilityResults, upgradeResults] = await Promise.all([
        axios.get(`${appConfig.apiURL}/api/jira/vulnerabilities`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${appConfig.apiURL}/api/jira/upgrades`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ])

      return {
        vulnerabilityIssues: processVulnerabilities(vulnerabilityResults),
        upgradeIssues: processUpgrades(upgradeResults)
      }
    } catch (err) {
      console.error(err)
      return rejectWithValue(false)
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState()?.api || {}
      if (state.vulnUpgradesLoading) return false
      const hasVuln = state.vulnerabilityTickets && Object.keys(state.vulnerabilityTickets).length > 0
      const hasUpgrades = state.upgradeTickets && Object.keys(state.upgradeTickets).length > 0
      return !hasVuln && !hasUpgrades
    }
  }
)

const fetchQbo = createAsyncThunk(
  'api/fetchQbo',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token
      const qboResults = await axios.get(`${appConfig.apiURL}/api/qbo`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const processed = processQbo(qboResults)
      return {
        qboByCustomer: processed.byCustomer
      }
    } catch (err) {
      console.error(err)
      return rejectWithValue(false)
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState()?.api || {}
      if (state.qboLoading) return false
      const hasQbo = state.qboByCustomer && Object.keys(state.qboByCustomer).length > 0
      return !hasQbo
    }
  }
)

/** Dependency-Track projects — fast path for first paint */
const DT_PROJECTS_TTL_MS = 5 * 60 * 1000
/** Bitbucket code-search mappings — expensive; longer cache */
const DT_MAPPINGS_TTL_MS = 20 * 60 * 1000

const isCacheFresh = (fetchedAt, ttlMs) =>
  typeof fetchedAt === 'number' && Date.now() - fetchedAt < ttlMs

const fetchDtProjects = createAsyncThunk(
  'api/fetchDtProjects',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token
      const results = await axios.get(`${appConfig.apiURL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const data = Array.isArray(results?.data) ? results.data : []
      return { dtProjects: data }
    } catch (err) {
      console.error(err)
      return rejectWithValue({ message: err?.message || 'Failed to load Dependency-Track projects' })
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const state = getState()?.api || {}
      if (state.dtProjectsLoading) return false
      if (!Array.isArray(state.dtProjects) || state.dtProjects.length === 0) return true
      return !isCacheFresh(state.dtProjectsFetchedAt, DT_PROJECTS_TTL_MS)
    }
  }
)

const fetchDtMappings = createAsyncThunk(
  'api/fetchDtMappings',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token
      const results = await axios.get(`${appConfig.apiURL}/api/projects/mappings`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const payload = results?.data && typeof results.data === 'object' ? results.data : {}
      return {
        dtMappings: {
          repositories: Array.isArray(payload.repositories) ? payload.repositories : [],
          orphans: Array.isArray(payload.orphans) ? payload.orphans : [],
          warnings: Array.isArray(payload.warnings) ? payload.warnings : []
        }
      }
    } catch (err) {
      console.error(err)
      return rejectWithValue({ message: err?.message || 'Failed to load Dependency-Track mappings' })
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const state = getState()?.api || {}
      if (state.dtMappingsLoading) return false
      if (state.dtMappingsFetchedAt == null) return true
      return !isCacheFresh(state.dtMappingsFetchedAt, DT_MAPPINGS_TTL_MS)
    }
  }
)

/** SOCR client trees — bulk catalog (no per-client detail fan-out) */
const SOCR_CLIENTS_TTL_MS = 5 * 60 * 1000
const SOCR_CODE_TREE_TTL_MS = 5 * 60 * 1000

const fetchSocrClients = createAsyncThunk(
  'api/fetchSocrClients',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token
      const treeRes = await axios.get(`${appConfig.apiURL}/api/socr/clients/tree`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const payload = treeRes?.data && typeof treeRes.data === 'object' ? treeRes.data : {}
      const clients = Array.isArray(payload.clients) ? payload.clients : []
      const catalog = buildSocrClientCatalog(clients)

      return {
        socrClientTree: clients,
        socrClientSettings: catalog.clientSettings,
        socrClientOptions: catalog.clientOptions,
        socrProjectAvailability: catalog.projectAvailability,
        socrN8nTabCapabilities: catalog.n8nTabCapabilities,
        socrProjectKeys: catalog.projectKeys
      }
    } catch (err) {
      if (err?.response?.status === 503) {
        return rejectWithValue({ message: 'SOCR service unavailable' })
      }
      console.error(err)
      return rejectWithValue({ message: err?.message || 'Failed to load SOCR clients' })
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const state = getState()?.api || {}
      if (state.socrClientsLoading) return false
      if (!state.socrCatalogReady) return true
      return !isCacheFresh(state.socrClientsFetchedAt, SOCR_CLIENTS_TTL_MS)
    }
  }
)

const fetchSocrCodeTree = createAsyncThunk(
  'api/fetchSocrCodeTree',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token
      const res = await axios.get(`${appConfig.apiURL}/api/socr/clients/code-tree`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const payload = res?.data && typeof res.data === 'object' ? res.data : {}
      return {
        socrCodeTree: Array.isArray(payload.clients) ? payload.clients : []
      }
    } catch (err) {
      if (err?.response?.status === 503) {
        return rejectWithValue({ message: 'SOCR service unavailable' })
      }
      console.error(err)
      return rejectWithValue({ message: err?.message || 'Failed to load SOCR code tree' })
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const state = getState()?.api || {}
      if (state.socrCodeTreeLoading) return false
      if (state.socrCodeTreeFetchedAt == null) return true
      return !isCacheFresh(state.socrCodeTreeFetchedAt, SOCR_CODE_TREE_TTL_MS)
    }
  }
)

/** UI phases for Refresh from DT: syncing-dt | loading-code-tree | null */
const setSocrDtRefreshPhase = createAction('api/setSocrDtRefreshPhase')

/** UI phases for Refresh from Bitbucket: syncing-bitbucket | loading-code-tree | null */
const setSocrBitbucketRefreshPhase = createAction('api/setSocrBitbucketRefreshPhase')

/**
 * POST /api/socr/repositories/refresh-dt — all repos
 * POST /api/socr/repositories/{uuid}/refresh-dt — one repo
 * Then reload code-tree + DT list. Phases: syncing-dt → loading-code-tree.
 * @param {{ repositoryUuid?: string }|undefined} arg
 */
const refreshSocrDtProjects = createAsyncThunk(
  'api/refreshSocrDtProjects',
  async (arg, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token
      const repositoryUuid = String(arg?.repositoryUuid || '').replace(/[{}]/g, '').trim()
      dispatch(setSocrDtRefreshPhase('syncing-dt'))

      const url = repositoryUuid
        ? `${appConfig.apiURL}/api/socr/repositories/${encodeURIComponent(repositoryUuid)}/refresh-dt`
        : `${appConfig.apiURL}/api/socr/repositories/refresh-dt`

      const res = await axios.post(
        url,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const result = res?.data && typeof res.data === 'object' ? res.data : {}

      // Code-tree is read-only — reload after the write completes
      dispatch(setSocrDtRefreshPhase('loading-code-tree'))
      await dispatch(fetchSocrCodeTree({ force: true })).unwrap()
      await dispatch(fetchDtProjects({ force: true })).unwrap()

      return { socrDtRefresh: result }
    } catch (err) {
      if (err?.response?.status === 404) {
        return rejectWithValue({ message: 'Repository not in SOCR catalog' })
      }
      if (err?.response?.status === 503) {
        return rejectWithValue({ message: 'SOCR service unavailable' })
      }
      console.error(err)
      const data = err?.response?.data
      const message =
        (typeof data === 'string' && data) ||
        data?.title ||
        data?.detail ||
        data?.message ||
        err?.message ||
        'Failed to refresh DT projects'
      return rejectWithValue({ message })
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState()?.api || {}
      return !state.socrDtRefreshLoading
    }
  }
)

/** Metadata sync stays under typical proxy idle limits. */
const BITBUCKET_METADATA_TIMEOUT_MS = 30 * 1000
/**
 * Runtime rescans must not hold a sync HTTP socket for minutes (ALB/CF 504, pool exhaustion).
 * Abort the client wait; API continues server-side — then reload/poll code-tree.
 */
const BITBUCKET_RUNTIME_CLIENT_WAIT_MS = 45 * 1000

const isBitbucketRefreshClientDisconnect = (err) => {
  const status = err?.response?.status
  if (status === 504 || status === 502 || status === 408) return true
  const code = err?.code
  if (code === 'ERR_CANCELED' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true
  const name = err?.name || ''
  if (name === 'CanceledError' || name === 'AbortError') return true
  return /timeout|aborted|canceled|cancelled|gateway/i.test(String(err?.message || ''))
}

const mergeAbortSignals = (signals) => {
  const list = (signals || []).filter(Boolean)
  if (!list.length) return undefined
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
    return AbortSignal.any(list)
  }
  const controller = new AbortController()
  const onAbort = () => controller.abort()
  for (const signal of list) {
    if (signal.aborted) {
      controller.abort()
      break
    }
    signal.addEventListener('abort', onAbort, { once: true })
  }
  return controller.signal
}

const delay = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(Object.assign(new Error('Aborted'), { code: 'ERR_CANCELED', name: 'CanceledError' }))
      return
    }
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        reject(Object.assign(new Error('Aborted'), { code: 'ERR_CANCELED', name: 'CanceledError' }))
      },
      { once: true }
    )
  })

/**
 * POST /api/socr/repositories/refresh-bitbucket — all repos (metadata by default)
 * POST /api/socr/repositories/{uuid}/refresh-bitbucket[?includeRuntimes=true] — one repo
 * Default updates name/slug only; includeRuntimes starts a long server-side .csproj scan.
 * Client never waits multi-minute on one socket: AbortSignal + short wait, then code-tree reload.
 * @param {{ repositoryUuid?: string, includeRuntimes?: boolean }|undefined} arg
 */
const refreshSocrBitbucket = createAsyncThunk(
  'api/refreshSocrBitbucket',
  async (arg, { getState, dispatch, rejectWithValue, signal }) => {
    const token = getState().auth.token
    const repositoryUuid = String(arg?.repositoryUuid || '').replace(/[{}]/g, '').trim()
    const includeRuntimes = Boolean(arg?.includeRuntimes)
    dispatch(setSocrBitbucketRefreshPhase('syncing-bitbucket'))

    const path = repositoryUuid
      ? `${appConfig.apiURL}/api/socr/repositories/${encodeURIComponent(repositoryUuid)}/refresh-bitbucket`
      : `${appConfig.apiURL}/api/socr/repositories/refresh-bitbucket`
    const params = new URLSearchParams()
    if (includeRuntimes) params.set('includeRuntimes', 'true')
    const qs = params.toString()
    const url = qs ? `${path}?${qs}` : path

    const requestSignal = includeRuntimes
      ? mergeAbortSignals([signal, AbortSignal.timeout(BITBUCKET_RUNTIME_CLIENT_WAIT_MS)])
      : signal

    const reloadCodeTree = async () => {
      dispatch(setSocrBitbucketRefreshPhase('loading-code-tree'))
      await dispatch(fetchSocrCodeTree({ force: true })).unwrap()
    }

    try {
      const res = await axios.post(
        url,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: includeRuntimes ? 0 : BITBUCKET_METADATA_TIMEOUT_MS,
          signal: requestSignal
        }
      )

      const result = res?.data && typeof res.data === 'object' ? res.data : {}
      await reloadCodeTree()
      return { socrBitbucketRefresh: { ...result, includeRuntimes } }
    } catch (err) {
      // Long runtime scans: client/proxy disconnect is expected; server keeps working.
      if (includeRuntimes && isBitbucketRefreshClientDisconnect(err) && !signal.aborted) {
        try {
          await reloadCodeTree()
          // Give the server a moment, then one follow-up read (still no long-held POST).
          await delay(8_000, signal)
          await dispatch(fetchSocrCodeTree({ force: true })).unwrap()
          return {
            socrBitbucketRefresh: {
              scope: repositoryUuid ? 'one' : 'all',
              includeRuntimes: true,
              continuedInBackground: true
            }
          }
        } catch (followUpErr) {
          if (signal.aborted || isBitbucketRefreshClientDisconnect(followUpErr)) {
            return rejectWithValue({ message: 'Bitbucket runtime sync cancelled' })
          }
          console.error(followUpErr)
          return rejectWithValue({
            message: followUpErr?.message || 'Failed to reload code tree after Bitbucket runtime sync'
          })
        }
      }

      if (signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return rejectWithValue({ message: 'Bitbucket sync cancelled' })
      }
      if (err?.response?.status === 404) {
        return rejectWithValue({ message: 'Repository not in SOCR catalog' })
      }
      if (err?.response?.status === 503) {
        return rejectWithValue({ message: 'SOCR service unavailable' })
      }
      console.error(err)
      const data = err?.response?.data
      const message =
        (typeof data === 'string' && data) ||
        data?.title ||
        data?.detail ||
        data?.message ||
        err?.message ||
        'Failed to refresh Bitbucket repositories'
      return rejectWithValue({ message })
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState()?.api || {}
      return !state.socrBitbucketRefreshLoading
    }
  }
)

const apiSlice = createSlice({
  name: 'api',
  initialState: {
    loading: false,
    vulnUpgradesLoading: false,
    qboLoading: false,
    organizationTickets: {},
    vulnerabilityTickets: {},
    upgradeTickets: {},
    qboByCustomer: {},
    fetchedAt: null,
    dtProjects: [],
    dtProjectsLoading: false,
    dtProjectsFetchedAt: null,
    dtProjectsError: null,
    dtMappings: { repositories: [], orphans: [], warnings: [] },
    dtMappingsLoading: false,
    dtMappingsFetchedAt: null,
    dtMappingsError: null,
    socrClientTree: [],
    socrClientSettings: {},
    socrClientOptions: {},
    socrProjectAvailability: {},
    socrN8nTabCapabilities: {},
    socrProjectKeys: [],
    socrCatalogReady: false,
    socrClientsLoading: false,
    socrClientsFetchedAt: null,
    socrClientsError: null,
    socrCodeTree: [],
    socrCodeTreeLoading: false,
    socrCodeTreeFetchedAt: null,
    socrCodeTreeError: null,
    socrDtRefreshLoading: false,
    socrDtRefreshPhase: null,
    socrDtRefreshResult: null,
    socrDtRefreshError: null,
    socrDtRefreshFetchedAt: null,
    /** When set, Refresh from DT is scoped to this repo UUID (else all). */
    socrDtRefreshRepositoryUuid: null,
    socrBitbucketRefreshLoading: false,
    socrBitbucketRefreshPhase: null,
    socrBitbucketRefreshResult: null,
    socrBitbucketRefreshError: null,
    socrBitbucketRefreshFetchedAt: null,
    socrBitbucketRefreshRepositoryUuid: null
  },
  reducers: {
    clearSocrDtRefreshResult: (state) => {
      state.socrDtRefreshResult = null
      state.socrDtRefreshError = null
      state.socrDtRefreshPhase = null
      state.socrDtRefreshRepositoryUuid = null
    },
    clearSocrBitbucketRefreshResult: (state) => {
      state.socrBitbucketRefreshResult = null
      state.socrBitbucketRefreshError = null
      state.socrBitbucketRefreshPhase = null
      state.socrBitbucketRefreshRepositoryUuid = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(setSocrDtRefreshPhase, (state, action) => {
        state.socrDtRefreshPhase = action.payload || null
      })
      .addCase(setSocrBitbucketRefreshPhase, (state, action) => {
        state.socrBitbucketRefreshPhase = action.payload || null
      })
      .addCase(fetchTickets.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.loading = false
        state.organizationTickets = action.payload?.orgZendeskTickets ?? {}
        state.fetchedAt = Date.now()
      })
      .addCase(fetchTickets.rejected, (state) => {
        state.loading = false
      })
      .addCase(fetchVulnerabilitiesAndUpgrades.pending, (state) => {
        state.vulnUpgradesLoading = true
      })
      .addCase(fetchVulnerabilitiesAndUpgrades.fulfilled, (state, action) => {
        state.vulnUpgradesLoading = false
        state.vulnerabilityTickets = action.payload?.vulnerabilityIssues ?? {}
        state.upgradeTickets = action.payload?.upgradeIssues ?? {}
      })
      .addCase(fetchVulnerabilitiesAndUpgrades.rejected, (state) => {
        state.vulnUpgradesLoading = false
      })
      .addCase(fetchQbo.pending, (state) => {
        state.qboLoading = true
      })
      .addCase(fetchQbo.fulfilled, (state, action) => {
        state.qboLoading = false
        state.qboByCustomer = action.payload?.qboByCustomer ?? {}
      })
      .addCase(fetchQbo.rejected, (state) => {
        state.qboLoading = false
      })
      .addCase(fetchDtProjects.pending, (state) => {
        state.dtProjectsLoading = true
        state.dtProjectsError = null
      })
      .addCase(fetchDtProjects.fulfilled, (state, action) => {
        state.dtProjectsLoading = false
        state.dtProjects = action.payload?.dtProjects ?? []
        state.dtProjectsFetchedAt = Date.now()
        state.dtProjectsError = null
      })
      .addCase(fetchDtProjects.rejected, (state, action) => {
        state.dtProjectsLoading = false
        state.dtProjectsError = action.payload?.message || 'Failed to load projects'
      })
      .addCase(fetchDtMappings.pending, (state) => {
        state.dtMappingsLoading = true
        state.dtMappingsError = null
      })
      .addCase(fetchDtMappings.fulfilled, (state, action) => {
        state.dtMappingsLoading = false
        state.dtMappings = action.payload?.dtMappings ?? { repositories: [], orphans: [], warnings: [] }
        state.dtMappingsFetchedAt = Date.now()
        state.dtMappingsError = null
      })
      .addCase(fetchDtMappings.rejected, (state, action) => {
        state.dtMappingsLoading = false
        state.dtMappingsError = action.payload?.message || 'Failed to load mappings'
      })
      .addCase(fetchSocrClients.pending, (state) => {
        state.socrClientsLoading = true
        state.socrClientsError = null
      })
      .addCase(fetchSocrClients.fulfilled, (state, action) => {
        state.socrClientsLoading = false
        state.socrClientTree = action.payload?.socrClientTree ?? []
        state.socrClientSettings = action.payload?.socrClientSettings ?? {}
        state.socrClientOptions = action.payload?.socrClientOptions ?? {}
        state.socrProjectAvailability = action.payload?.socrProjectAvailability ?? {}
        state.socrN8nTabCapabilities = action.payload?.socrN8nTabCapabilities ?? {}
        state.socrProjectKeys = action.payload?.socrProjectKeys ?? []
        state.socrCatalogReady = true
        state.socrClientsFetchedAt = Date.now()
        state.socrClientsError = null
      })
      .addCase(fetchSocrClients.rejected, (state, action) => {
        state.socrClientsLoading = false
        state.socrCatalogReady = false
        state.socrClientsError = action.payload?.message || 'Failed to load SOCR clients'
      })
      .addCase(fetchSocrCodeTree.pending, (state) => {
        state.socrCodeTreeLoading = true
        state.socrCodeTreeError = null
      })
      .addCase(fetchSocrCodeTree.fulfilled, (state, action) => {
        state.socrCodeTreeLoading = false
        state.socrCodeTree = action.payload?.socrCodeTree ?? []
        state.socrCodeTreeFetchedAt = Date.now()
        state.socrCodeTreeError = null
      })
      .addCase(fetchSocrCodeTree.rejected, (state, action) => {
        state.socrCodeTreeLoading = false
        state.socrCodeTreeError = action.payload?.message || 'Failed to load SOCR code tree'
      })
      .addCase(refreshSocrDtProjects.pending, (state, action) => {
        state.socrDtRefreshLoading = true
        state.socrDtRefreshPhase = 'syncing-dt'
        state.socrDtRefreshError = null
        state.socrDtRefreshResult = null
        state.socrDtRefreshRepositoryUuid =
          String(action.meta?.arg?.repositoryUuid || '').replace(/[{}]/g, '').trim() || null
      })
      .addCase(refreshSocrDtProjects.fulfilled, (state, action) => {
        state.socrDtRefreshLoading = false
        state.socrDtRefreshPhase = null
        state.socrDtRefreshResult = action.payload?.socrDtRefresh ?? null
        state.socrDtRefreshFetchedAt = Date.now()
        state.socrDtRefreshError = null
        state.socrDtRefreshRepositoryUuid = null
      })
      .addCase(refreshSocrDtProjects.rejected, (state, action) => {
        state.socrDtRefreshLoading = false
        state.socrDtRefreshPhase = null
        state.socrDtRefreshError = action.payload?.message || 'Failed to refresh DT projects'
        state.socrDtRefreshRepositoryUuid = null
      })
      .addCase(refreshSocrBitbucket.pending, (state, action) => {
        state.socrBitbucketRefreshLoading = true
        state.socrBitbucketRefreshPhase = 'syncing-bitbucket'
        state.socrBitbucketRefreshError = null
        state.socrBitbucketRefreshResult = null
        state.socrBitbucketRefreshRepositoryUuid =
          String(action.meta?.arg?.repositoryUuid || '').replace(/[{}]/g, '').trim() || null
      })
      .addCase(refreshSocrBitbucket.fulfilled, (state, action) => {
        state.socrBitbucketRefreshLoading = false
        state.socrBitbucketRefreshPhase = null
        state.socrBitbucketRefreshResult = action.payload?.socrBitbucketRefresh ?? null
        state.socrBitbucketRefreshFetchedAt = Date.now()
        state.socrBitbucketRefreshError = null
        state.socrBitbucketRefreshRepositoryUuid = null
      })
      .addCase(refreshSocrBitbucket.rejected, (state, action) => {
        state.socrBitbucketRefreshLoading = false
        state.socrBitbucketRefreshPhase = null
        state.socrBitbucketRefreshError = action.payload?.message || 'Failed to refresh Bitbucket repositories'
        state.socrBitbucketRefreshRepositoryUuid = null
      })
  }
})

export const actions = {
  fetchTickets,
  fetchVulnerabilitiesAndUpgrades,
  fetchQbo,
  fetchDtProjects,
  fetchDtMappings,
  fetchSocrClients,
  fetchSocrCodeTree,
  refreshSocrDtProjects,
  refreshSocrBitbucket,
  ...apiSlice.actions
}
export const { reducer } = apiSlice
